import { FC, useCallback, useState } from 'react'
import { GlassCircle } from '@/atoms/GlassCircle'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { Image } from '@/atoms/Image'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { Seo } from '@/layout/Seo'
import { AngelPlayer, createAsset } from '@/organisms/AngelPlayer'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'

interface CustomExclusiveSceneViewProps {
  videoUrl: string
  imageUrl: string
  canonical: string
  title: string
  description: string
  logoUrl: string
  projectSlug: string
}

const CustomExclusiveSceneView: FC<CustomExclusiveSceneViewProps> = ({
  videoUrl,
  imageUrl,
  canonical,
  title,
  description,
  logoUrl,
  projectSlug,
}) => {
  const track = useSafeTrack()
  const [isPlayerLoaded, setPlayerLoaded] = useState<boolean>(false)

  const trackVideoStart = useCallback(() => {
    track('Video Content Started', {
      position: 0,
      program: projectSlug,
      publisher: 'angel-studios',
      title: 'Sneak Peek',
      video_type: 'video',
    })
  }, [projectSlug, track])

  const trackVideoCompleted = useCallback(() => {
    track('Video Content Completed', {
      position: 0,
      program: projectSlug,
      publisher: 'angel-studios',
      title: 'Sneak Peek',
      video_type: 'video',
    })
  }, [projectSlug, track])

  const asset = createAsset({
    url: videoUrl,
    guid: `${projectSlug}-exclusive-scene`,
    name: 'Exclusive Scene',
    slug: projectSlug,
    videoType: 'episode',
    title: title,
    subtitle: title,
  })

  return (
    <div>
      <Seo nofollow noindex description={description} openGraph={{}} title={title} canonical={canonical} />
      <ResponsiveBackgroundImage
        src="/v1689354516/angel-app/assets/discover/background_blur_v2.webp"
        preBackgroundStyles="linear-gradient(180deg, rgba(21, 21, 21, 0.3) 50%, #151515 100%), "
        className="pointer-events-none h-[75vh] touch-none select-none sm:h-screen"
      />
      <div className="relative mx-8">
        <Image
          src={getCloudinaryImageUrl({
            path: logoUrl,
          })}
          alt={title}
          height={200}
          width={200}
          className="order-0 z-50 col-span-12 mx-auto h-[120px] w-auto sm:col-span-10 sm:col-start-2 sm:h-[150px] lg:col-span-4 lg:col-start-2"
        />
        <div className="order-1 col-span-12 mb-8 max-w-[1200px] md:order-2 md:col-span-10 md:col-start-2 lg:col-span-5 lg:col-start-7 lg:row-span-2 lg:row-start-1 lg:mx-auto lg:self-center">
          {isPlayerLoaded && (
            <AngelPlayer
              asset={asset}
              shouldSaveWatchPosition={false}
              autoplay={true}
              onStart={trackVideoStart}
              onPlaybackFinished={trackVideoCompleted}
              className="aspect-h-9 aspect-w-16 relative z-10 overflow-hidden rounded-2xl"
            />
          )}
          {!isPlayerLoaded && (
            <div className="relative cursor-pointer" onClick={() => setPlayerLoaded(true)}>
              <div className="aspect-h-9 aspect-w-16 relative overflow-hidden rounded-2xl">
                <Image src={imageUrl} alt="Cabrini Bonus Scene" fill priority />
              </div>
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <GlassCircle size={60}>
                  <PlayIcon size={30} />
                </GlassCircle>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export { CustomExclusiveSceneView }
